import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BillingService, BuildingOrg, Contract, ContractId, Org, OrgId } from 'shared/models';
import { propertyNameLabelKeys } from '../orgRoles';
import { OrgRoleOption, OrgRoleOptionData } from './orgRoleOption/OrgRoleOption';
import { ErrorMessage, WarningMessage } from 'ui/shared/messages';
import { useForm, useSession } from 'hooks';
import { Permission } from 'shared/permissions';
import { OrgSelectField } from 'ui/shared/controls/orgSelectField/OrgSelectField';
import { OrgContractField } from 'ui/admin/orgs/orgForm/orgContracts/orgContractField/OrgContractField';
import { sortNames } from 'logic/sortNames';
import { CheckField } from 'ui/shared/controls';
import { BillingStrings } from 'ui/contracts/contracts';

require('./BuildingOrgForm.css');

export type FormData = {
    orgId: OrgId | null;
    contractId: ContractId | null;
    properties: BuildingOrg['properties'];
    replace: BuildingOrg['properties'];
};

export type Props = {
    isNew: boolean;
    buildingOrgs: Record<OrgId, BuildingOrg>;
    org: Org | null;
    contractIds: ContractId[];
    contracts: Record<ContractId, Contract>;
    hasEcs: boolean;
    busy: boolean;
    errorMessage?: string;
    onOrgChanged(orgId: OrgId | null): void;
    onConfigure(): void;
    onSave(data: FormData): void;
    onCancel(): void;
};

const nullValue = 'none';

const mustExistRoles: BuildingOrg['properties'] = {
    isContractor: true,
    isCallMonitor: true, // only must exist in ECS building (but cannot exist in non-ECS building)
};

function getInitialRoleOptions(buildingOrgs: Record<OrgId, BuildingOrg>, org: Org | null, hasEcs: boolean, isAdmin: boolean): OrgRoleOptionData[] {
    const buildingOrg = (org?.id && buildingOrgs[org.id]) || null;

    // Format the known org role types into a data structure for use in
    // the OrgRoleOption components
    const data: Omit<OrgRoleOptionData, 'replaceNames'>[] =
        (Object.keys(propertyNameLabelKeys) as (keyof BuildingOrg['properties'])[])
            .map(propertyName => ({
                labelKey: propertyNameLabelKeys[propertyName],
                propertyName,
                visible:
                    ((propertyName === 'isCallMonitor' && (hasEcs || buildingOrg?.properties[propertyName])) || propertyName !== 'isCallMonitor') && (
                        !org ||
                        !!org?.properties[propertyName] ||
                        (!!buildingOrg?.properties[propertyName] && mustExistRoles[propertyName]) ||
                        isAdmin
                    ),

                replaceFirst: !!(
                    buildingOrg?.properties[propertyName] &&
                    Object.values(buildingOrgs).filter(bo => bo.properties[propertyName]).length === 1 &&
                    mustExistRoles[propertyName]
                ),

                disabled: (!hasEcs && propertyName === 'isCallMonitor' && !buildingOrg?.properties[propertyName]) || (
                    !isAdmin && org && !org.properties[propertyName] || (
                        buildingOrg?.properties[propertyName] &&
                        Object.values(buildingOrgs).filter(bo => bo.properties[propertyName]).length === 1 &&
                        mustExistRoles[propertyName]
                    )
                ),
            })).filter(ro => ro.visible);

    return data.map(d => ({
        ...d,
        // Collect the Org names of any of the existing building-org links
        // that are configured to have the current isXxxxx property.
        // (This is just used in the UI to display a label)
        replaceNames:
            org?.id && buildingOrgs[org.id]?.properties[d.propertyName]
                ? [] // Don't show the replace prompt if this org already has this role
                : Object.values(buildingOrgs)
                    .filter(bo => bo.properties[d.propertyName])
                    .filter(bo => bo.orgId !== org?.id)
                    .filter(bo => !org?.properties[d.propertyName])
                    .map(bo => bo.displayName)
                    .sort(sortNames),
    }));
}

export const BuildingOrgForm = ({ isNew, org, contractIds, contracts, busy, buildingOrgs, errorMessage, hasEcs, onOrgChanged, onConfigure, onSave, onCancel }: Props) => {
    const { t } = useTranslation();
    const { hasPermission } = useSession();

    const buildingOrg = (org?.id && buildingOrgs[org.id]) || null;

    const initialFormData = useMemo(() => ({
        orgId: org?.id || null,
        contractId: buildingOrg?.contractId || null,
        properties: buildingOrg?.properties || {},
        replace: {},
    }), []); // Intentionally empty

    const { data, handleChange, handleSubmit } = useForm<FormData>('building-org', initialFormData, onSave, { nullValue, undefinedFalse: true });

    const isAdmin = hasPermission(null, Permission.Admin);

    const roleOptions = useMemo(() => {
        return getInitialRoleOptions(buildingOrgs, org, hasEcs, isAdmin);
    }, [ buildingOrgs, org, hasEcs, isAdmin ]);

    // Inform our parent if the selected Org changed so it can supply us with new Contracts
    useEffect(() => {
        onOrgChanged(data.orgId);
    }, [ data.orgId, onOrgChanged ]);

    const orgsToExclude = Object.keys(buildingOrgs || {});
    const sortedEligibleContractIds = contractIds
        .filter(id =>
            hasEcs || (
                !contracts[id].services[BillingService.RemoteAccess]
                && !contracts[id].services[BillingService.PhoneAnswering]
            )
        ).sort((a, b) => contracts[b].created - contracts[a].created);
    
    // Unchecking all options will cause the building-org link to be deleted
    const willDelete = !isNew && !Object.values(data.properties).length;

    return (
        <form
            id="building-org-form"
            onSubmit={ handleSubmit }
        >
            { isNew && hasPermission(null, Permission.AllOrganizations) &&
                <OrgSelectField
                    id="link-building-org"
                    name="orgId"
                    className="link-building-org"
                    labelKey="BUILDING.ORGS.LABEL.ORGANIZATION"
                    value={ org?.id || nullValue }
                    orgsToExclude={ orgsToExclude }
                    emptyOptionTextKey="BUILDING.ORGS.LABEL.SELECT_ORGANIZATION"
                    onChange={ handleChange }
                />
            }
            <div className="allowed-roles">
                <label>
                    { t('BUILDING.ORGS.LABEL.ALLOWED_ROLES') }
                </label>
                <ul>
                    { roleOptions.length === 0 &&
                        <li className="org-role-option">
                            <CheckField
                                id="org-role-option-none"
                                labelKey="LABEL.NONE"
                                disabled={ true }
                                input={ {
                                    name: 'properties',
                                    checked: false,
                                } }
                            />
                        </li>
                    }
                    { roleOptions.map(option =>
                        <OrgRoleOption
                            key={ option.labelKey }
                            { ...option }
                            data={ data }
                            onChange={ handleChange }
                        />
                    ) }
                </ul>
                { willDelete &&
                    <WarningMessage
                        message={ t('BUILDING.ORGS.LABEL.DELETE_WARNING') }
                    />
                }
            </div>
            { hasPermission(null, Permission.Admin) &&
                <OrgContractField
                    id="building-org-contract"
                    name="contractId"
                    label={ BillingStrings.BILLING_CONTRACT }
                    loading={ busy }
                    contracts={ contracts }
                    contractIds={ sortedEligibleContractIds }
                    value={ data.contractId }
                    disabled={ busy || !org }
                    readOnly={ !hasPermission(null, Permission.ManageBilling) }
                    onChange={ handleChange }
                    onConfigure={ onConfigure }
                />
            }
            <div className="footer">
                { (!isNew || hasPermission(null, Permission.AllOrganizations)) &&
                    <button
                        id="save-button"
                        type="submit"
                        className="ui primary red button"
                        disabled={ busy || (isNew && !Object.keys(data.properties).length) || !org?.id }
                    >
                        { t('COMMAND.SAVE') }
                    </button>
                }
                { isNew && !hasPermission(null, Permission.AllOrganizations) &&
                    <button
                        id="generate-link-button"
                        type="submit"
                        className="ui primary red button"
                        disabled={ busy || !Object.keys(data.properties).length }
                    >
                        { t('COMMAND.GENERATE_LINK') }
                    </button>
                }
                <button
                    id="cancel-button"
                    type="button"
                    className="ui grey button"
                    disabled={ busy }
                    onClick={ onCancel }
                >
                    { t('COMMAND.CANCEL') }
                </button>
            </div>
            <ErrorMessage message={ errorMessage }/>
        </form>
    );
};
